<script setup lang="ts">
import { ref } from "vue";
import { useRouter } from "vue-router";
import { useAuthStore } from "./modules/auth/store";
import { computed } from "vue";
import ToastContainer from "./components/toast.vue";
import ScrollToTop from "./components/scroll-to-top.vue";

const router = useRouter();
const authStore = useAuthStore();
const mobileMenuOpen = ref(false);

const isAuthenticated = computed(() => !!authStore.token);

function handleSignIn() {
  mobileMenuOpen.value = false;
  router.push({ name: "login" });
}

async function handleLogout() {
  mobileMenuOpen.value = false;
  await authStore.logout();
  router.push({ name: "home" });
}
</script>

<template>
  <div class="noise-bg relative min-h-screen flex flex-col" style="background: linear-gradient(180deg, var(--color-bg-primary) 0%, var(--color-bg-dark) 100%);">
    <a href="#main-content" class="sr-only focus:not-sr-only focus:absolute focus:top-2 focus:left-2 focus:z-50 focus:px-4 focus:py-2 focus:bg-white focus:text-slate-900 focus:rounded-lg focus:text-sm focus:font-medium">
      Skip to content
    </a>

    <nav class="fixed top-0 left-0 right-0 z-20 px-4 py-4 md:px-6 md:py-6 backdrop-blur-md bg-[var(--color-bg-primary)]/70">
      <div class="max-w-7xl mx-auto flex items-center justify-between">
        <router-link to="/" class="flex items-center gap-3">
          <div
            class="w-10 h-10 rounded-xl flex items-center justify-center shadow-lg"
            :style="{
              background: 'var(--color-brand-gradient)',
              boxShadow: '0 10px 15px -3px var(--color-brand-20)',
            }"
          >
            <svg class="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2.5" d="M13 10V3L4 14h7v7l9-11h-7z" />
            </svg>
          </div>
          <span class="text-2xl font-bold text-white">Autom8n</span>
        </router-link>

        <!-- Desktop nav -->
        <div class="hidden md:flex items-center gap-4">
          <router-link to="/#features" class="text-slate-400 hover:text-white transition-colors font-medium py-2">Features</router-link>
          <router-link to="/#how-it-works" class="text-slate-400 hover:text-white transition-colors font-medium py-2">How it works</router-link>
          <router-link to="/contact" class="text-slate-400 hover:text-white transition-colors font-medium py-2">Contact</router-link>
          <button v-if="!isAuthenticated" @click="handleSignIn"
            class="px-5 py-2.5 text-white rounded-lg transition-all font-medium shadow-lg hover:brightness-110 cursor-pointer"
            :style="{
              backgroundColor: 'var(--color-brand)',
              boxShadow: '0 10px 15px -3px var(--color-brand-10)',
            }">
            Sign in
          </button>
          <button v-else @click="handleLogout"
            class="px-5 py-2.5 text-slate-400 hover:text-white rounded-lg transition-all font-medium cursor-pointer">
            Sign out
          </button>
        </div>

        <!-- Mobile hamburger -->
        <div class="md:hidden flex items-center gap-2">
          <button
            class="p-2 rounded-lg text-slate-400 hover:text-white hover:bg-white/10 transition-colors cursor-pointer"
            @click="mobileMenuOpen = !mobileMenuOpen"
            aria-label="Toggle menu"
          >
            <svg v-if="!mobileMenuOpen" class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 6h16M4 12h16M4 18h16" />
            </svg>
            <svg v-else class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
      </div>

      <!-- Mobile menu -->
      <Transition name="mobile-menu">
        <div v-if="mobileMenuOpen" class="md:hidden mt-4 rounded-xl glass-card overflow-hidden">
          <router-link to="/#features" @click="mobileMenuOpen = false" class="block px-5 py-3.5 text-slate-300 hover:text-white hover:bg-white/5 transition-colors font-medium">Features</router-link>
          <router-link to="/#how-it-works" @click="mobileMenuOpen = false" class="block px-5 py-3.5 text-slate-300 hover:text-white hover:bg-white/5 transition-colors font-medium">How it works</router-link>
          <router-link to="/contact" @click="mobileMenuOpen = false" class="block px-5 py-3.5 text-slate-300 hover:text-white hover:bg-white/5 transition-colors font-medium">Contact</router-link>
          <div class="border-t border-white/5">
            <button v-if="!isAuthenticated" @click="handleSignIn"
              class="w-full px-5 py-3.5 text-white font-medium text-left cursor-pointer"
              :style="{ backgroundColor: 'var(--color-brand)' }">
              Sign in
            </button>
            <button v-else @click="handleLogout"
              class="w-full px-5 py-3.5 text-slate-300 hover:text-white font-medium text-left hover:bg-white/5 transition-colors cursor-pointer">
              Sign out
            </button>
          </div>
        </div>
      </Transition>
    </nav>

    <main id="main-content" class="relative flex-1 flex items-center justify-center px-4 md:px-6 pt-24 pb-16">
      <RouterView v-slot="{ Component }">
        <Transition name="route" mode="out-in">
          <component :is="Component" />
        </Transition>
      </RouterView>
    </main>

    <footer class="relative border-t border-white/10 pt-12 pb-8" :style="{ backgroundColor: 'var(--color-bg-elevated)' }">
      <div class="max-w-7xl mx-auto px-4 md:px-6">
        <!-- Top section: brand + nav columns -->
        <div class="grid grid-cols-1 md:grid-cols-3 gap-10 mb-10">
          <!-- Brand column -->
          <div>
            <div class="flex items-center gap-3 mb-4">
              <div class="w-10 h-10 rounded-xl flex items-center justify-center" :style="{ background: 'var(--color-brand-gradient)' }">
                <svg class="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2.5" d="M13 10V3L4 14h7v7l9-11h-7z" />
                </svg>
              </div>
              <span class="text-xl font-bold text-white">Autom8n</span>
            </div>
            <p class="text-sm text-slate-400 leading-relaxed mb-4">
              The AI-powered platform that transforms your workflow descriptions into ready-to-use n8n automations.
            </p>
            <p class="text-sm font-semibold text-[var(--color-brand-accent)]">
              Solve once. Automate forever.
            </p>
          </div>

          <!-- Product column -->
          <div>
            <h4 class="text-sm font-semibold text-white mb-4">Product</h4>
            <ul class="space-y-2.5 text-sm text-slate-400">
              <li><router-link to="/#features" class="hover:text-white transition-colors">Features</router-link></li>
              <li><router-link to="/#how-it-works" class="hover:text-white transition-colors">How It Works</router-link></li>
              <li><a href="#benefits" class="hover:text-white transition-colors">Benefits</a></li>
            </ul>
          </div>

          <!-- Resources column -->
          <div>
            <h4 class="text-sm font-semibold text-white mb-4">Resources</h4>
            <ul class="space-y-2.5 text-sm text-slate-400">
              <li><a href="https://docs.n8n.io/" target="_blank" rel="noopener" class="hover:text-white transition-colors">n8n Documentation</a></li>
              <li><a href="https://n8n.io/" target="_blank" rel="noopener" class="hover:text-white transition-colors">About n8n</a></li>
            </ul>
          </div>
        </div>

        <!-- Bottom bar -->
        <div class="border-t border-white/5 pt-6 flex flex-col md:flex-row items-center justify-between gap-4">
          <p class="text-xs text-slate-500">&copy; 2026 ShipWithAI. All rights reserved.</p>
          <div class="flex items-center gap-6 text-xs text-slate-500">
            <a href="#" class="hover:text-slate-300 transition-colors">Privacy Policy</a>
            <a href="#" class="hover:text-slate-300 transition-colors">Terms of Service</a>
          </div>
        </div>
      </div>
    </footer>

    <ToastContainer />
    <ScrollToTop />
  </div>
</template>